import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  LabelList,
  Legend,
} from "recharts";

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="p-2 bg-white border rounded shadow text-sm text-gray-700">
        <p className="font-semibold text-indigo-700">{label}</p>
        {payload.map((entry, index) => (
          <p key={`item-${index}`} style={{ color: entry.color }}>
            {entry.dataKey === "completed" ? "✅ Completed" : "⏳ Pending"}: <strong>{entry.value}</strong>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const DailyBoxingGraphModal = ({ data = [], onClose, orderTotals = {} }) => {
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().split("T")[0]);

  const groupedByOrder = {};
  data.forEach((item) => {
    if (item.date !== selectedDate) return;

    const order = item.cut_order_no || `Order-${item.order_id}`;
    const colorList = item.suborder_colors?.map(c => c.suborder_colour_name) || [item.suborder_colour_name || "Unknown"];

    if (!groupedByOrder[order]) groupedByOrder[order] = {};

    colorList.forEach((colorName) => {
      const normalizedColor = colorName?.trim().toLowerCase();
      if (!groupedByOrder[order][normalizedColor]) {
        groupedByOrder[order][normalizedColor] = 0;
      }
      groupedByOrder[order][normalizedColor] += parseInt(item.total_piece);
    });
  });

  const charts = Object.entries(orderTotals).map(([order, plannedColors]) => {
    const chartData = Object.entries(plannedColors).map(([color, planned]) => {
      const normalizedColor = color?.trim().toLowerCase();
      const completed = groupedByOrder?.[order]?.[normalizedColor] || 0;
      const pending = Math.max(planned - completed, 0);

      return {
        label: color,
        completed,
        pending,
        planned,
      };
    });

    const totalPlanned = chartData.reduce((sum, d) => sum + d.planned, 0);
    const totalCompleted = chartData.reduce((sum, d) => sum + d.completed, 0);
    const totalPending = chartData.reduce((sum, d) => sum + d.pending, 0);
    const maxY = Math.max(...chartData.map((d) => d.planned), 100);

    return (
      <div
        key={order}
        className="bg-white border rounded-lg shadow p-4 w-full"
      >
        <h3 className="text-md font-semibold text-indigo-700 mb-2">{order}</h3>
        <p className="text-sm text-gray-600 mb-1">📅 Date: <strong>{selectedDate}</strong></p>
        <p className="text-sm text-gray-700 mb-3">
          📦 <strong>Order Quantity:</strong> {totalPlanned} &nbsp; | &nbsp;
          ✅ <strong>Completed:</strong> {totalCompleted} &nbsp; | &nbsp;
          ⏳ <strong>Pending:</strong> {totalPending}
        </p>

        <ResponsiveContainer width="100%" height={330}>
          <BarChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" angle={-20} textAnchor="end" interval={0} height={40} />
            <YAxis domain={[0, maxY]} />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              layout="vertical"
              verticalAlign="bottom"
              align="left"
              iconType="circle"
              wrapperStyle={{
                paddingTop: 10,
                lineHeight: '22px',
              }}
            />
            <Bar dataKey="completed" stackId="a" fill="#4caf50" name="✅ Completed" barSize={40}>
              <LabelList dataKey="planned" position="top" fontSize={12} />
            </Bar>
            <Bar dataKey="pending" stackId="a" fill="#ff6f61" name="⏳ Pending" barSize={40}>
              <LabelList
                dataKey="pending"
                position="top"
                fontSize={12}
                formatter={(val) => (val > 0 ? val : "")}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  });

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center">
      <div className="bg-white w-full h-full p-6 overflow-y-auto relative rounded-none shadow-none">
        <button
          className="absolute top-4 right-6 text-2xl text-gray-500 hover:text-red-500"
          onClick={onClose}
        >
          ✖
        </button>
        <h2 className="text-2xl font-bold text-indigo-800 mb-6">
          📊 Daily Boxing Charts
        </h2>

        <div className="mb-4">
          <label className="text-sm font-medium text-gray-700 mr-2">Select Date:</label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="border px-3 py-1 rounded-md shadow-sm"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-6">
          {charts.length > 0 ? charts : (
            <p className="text-gray-500">No planned boxing data found.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DailyBoxingGraphModal;
